import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Trophy, Star, Heart, Zap, Target, Award, TrendingUp, Users, Plus } from 'lucide-react';
import { pb } from '@/lib/pocketbase';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { isAdmin, isTutor } from '@/utils/roleValidation';
import { getProfilePictureUrl } from '@/utils/profileUtils';
import { useNavigate } from 'react-router-dom';

interface SuccessStoryFormProps {
  trigger?: React.ReactNode;
  openModal?: boolean;
  onModalOpenChange?: (open: boolean) => void;
}

const iconOptions = [
  { value: 'trophy', label: 'Trophy', icon: Trophy },
  { value: 'star', label: 'Star', icon: Star },
  { value: 'heart', label: 'Heart', icon: Heart },
  { value: 'zap', label: 'Zap', icon: Zap },
  { value: 'target', label: 'Target', icon: Target },
  { value: 'award', label: 'Award', icon: Award },
  { value: 'trending', label: 'Trending', icon: TrendingUp },
  { value: 'users', label: 'Users', icon: Users }
];

const subjectOptions = [
  'fitness', 'music', 'programming', 'english', 'travel'
];

const SuccessStoryForm = ({ trigger, openModal, onModalOpenChange }: SuccessStoryFormProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    subject: '',
    icon_name: 'star',
    tutor_id: ''
  });
  const [tutors, setTutors] = useState<any[]>([]);
  const [currentTutorId, setCurrentTutorId] = useState<string>('');
  const [tutorProfile, setTutorProfile] = useState<any>(null);
  // Handle programmatic opening
  useEffect(() => {
    if (openModal && user) {
      setIsOpen(true);
      if (onModalOpenChange) {
        onModalOpenChange(true);
      }
    }
  }, [openModal, user, onModalOpenChange]);

  // Fetch tutors for admin selection and get current tutor ID if user is a tutor
  useEffect(() => {
    const fetchData = async () => {
      try {
        // If user is a tutor, get their tutor ID
        if (isTutor(user)) {
          try {
            const profile = await pb.collection('tutors').getFirstListItem(`user = "${user.id}"`);
            setCurrentTutorId(profile.id);
            setTutorProfile(profile);
            console.log('Found tutor profile:', profile.id);
            
            // Check profile picture when opening modal
            if (isOpen && !getProfilePictureUrl(profile)) {
              toast({
                title: "Complete your profile first",
                description: "You need to complete your profile before creating success stories.",
                variant: "destructive"
              });
              setIsOpen(false);
              if (onModalOpenChange) {
                onModalOpenChange(false);
              }
              navigate('/profile');
            }
          } catch (error) {
            console.error('Error fetching tutor profile:', error);
          }
        }

        // If user is admin, fetch all tutors for selection
        if (isAdmin(user)) {
          const tutorsList = await pb.collection('tutors').getFullList({
            sort: 'name',
            limit: 100
          });
          setTutors(tutorsList);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    if (isOpen && user) {
      fetchData();
    }
  }, [isOpen, user, navigate, toast, onModalOpenChange]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to share your success story.",
        variant: "destructive"
      });
      return;
    }

    // Check profile picture for tutors before submission
    if (isTutor(user)) {
      if (!tutorProfile || !getProfilePictureUrl(tutorProfile)) {
        toast({
          title: "Complete your profile first",
          description: "You need to complete your profile before creating success stories.",
          variant: "destructive"
        });
        setIsOpen(false);
        if (onModalOpenChange) {
          onModalOpenChange(false);
        }
        navigate('/profile');
        return;
      }
    }

    if (!formData.title.trim() || !formData.description.trim() || !formData.subject) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const tutorIdToSubmit = isTutor(user) ? currentTutorId : (formData.tutor_id === 'none' ? '' : formData.tutor_id);
      
      await pb.collection('success_stories').create({
        title: formData.title.trim(),
        description: formData.description.trim(),
        subject: formData.subject,
        icon_name: formData.icon_name,
        user_name: user.name || 'Anonymous',
        user_id: user.id,
        tutor_id: tutorIdToSubmit,
        is_approved: false // Requires admin approval
      });

      toast({
        title: "Success Story Submitted! 🎉",
        description: "Your story is now under review by our admin team and will appear once approved.",
        duration: 5000
      });

      // Reset form
      setFormData({
        title: '',
        description: '',
        subject: '',
        icon_name: 'star',
        tutor_id: ''
      });
      
      setIsOpen(false);
    } catch (error) {
      console.error('Error submitting success story:', error);
      toast({
        title: "Submission Failed",
        description: "Failed to submit your success story. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const defaultTrigger = (
    <Button variant="outline" className="w-full">
      <Plus className="w-4 h-4 mr-2" />
      Share Success Story
    </Button>
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Share Your Success Story</DialogTitle>
          <DialogDescription>
            Tell the community about your achievements and inspire others on their learning journey.
          </DialogDescription>
        </DialogHeader>
        
        <Card className="border-0 shadow-none">
          <CardContent className="p-0">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  placeholder="e.g., Completed my first marathon!"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  maxLength={100}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Share the details of your achievement and how you got there..."
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  maxLength={300}
                  rows={3}
                  required
                />
                <p className="text-xs text-gray-500">
                  {formData.description.length}/300 characters
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Subject *</Label>
                <Select 
                  value={formData.subject} 
                  onValueChange={(value) => handleInputChange('subject', value)}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {subjectOptions.map((subject) => (
                      <SelectItem key={subject} value={subject}>
                        {subject.charAt(0).toUpperCase() + subject.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Only show tutor selection for admins */}
              {isAdmin(user) && (
                <div className="space-y-2">
                  <Label htmlFor="tutor">Tutor (Optional)</Label>
                  <Select 
                    value={formData.tutor_id} 
                    onValueChange={(value) => handleInputChange('tutor_id', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a tutor (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No specific tutor</SelectItem>
                      {tutors.map((tutor) => (
                        <SelectItem key={tutor.id} value={tutor.id}>
                          {tutor.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Show info for tutors that their ID will be auto-assigned */}
              {isTutor(user) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-700">
                    📝 Your tutor profile will be automatically linked to this success story.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="icon">Icon</Label>
                <Select 
                  value={formData.icon_name} 
                  onValueChange={(value) => handleInputChange('icon_name', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map((option) => {
                      const IconComponent = option.icon;
                      return (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            <IconComponent className="h-4 w-4" />
                            {option.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Story'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};

export default SuccessStoryForm;